/**
 * Fullscreen image lightbox (TNK-115 phase B2). Opened when a buyer
 * taps the card photo from either the modal or the full /shop/card/$id
 * page — lets them inspect the actual card without the trust-signal pill
 * or the rest of the layout obstructing the photo.
 *
 * z-60 so it sits above the modal (which is z-50). Click backdrop or
 * press Esc to close. When the card has both faces, a small Front / Back
 * toggle sits in the bottom-center.
 */
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { RefreshCw, X } from "lucide-react";

import { imageUrl } from "@/features/imageUpload";

interface CardLightboxProps {
  /** When `null`, the lightbox is closed. */
  card: {
    name: string;
    imageFrontRef: string | null;
    imageBackRef: string | null;
  } | null;
  onClose: () => void;
}

export function CardLightbox({ card, onClose }: CardLightboxProps) {
  const [showBack, setShowBack] = useState(false);

  useEffect(() => {
    setShowBack(false);
  }, [card?.imageFrontRef]);

  useEffect(() => {
    if (!card) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [card, onClose]);

  if (!card) return null;

  const frontUrl = card.imageFrontRef ? imageUrl(card.imageFrontRef) : null;
  const backUrl = card.imageBackRef ? imageUrl(card.imageBackRef) : null;
  const url = showBack && backUrl ? backUrl : frontUrl;

  return (
    <AnimatePresence>
      <motion.div
        key="lightbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        className="fixed inset-0 z-[60] flex flex-col items-center justify-center p-4"
        style={{ background: "rgba(2,3,8,0.95)", backdropFilter: "blur(8px)" }}
        onClick={onClose}
      >
        {/* Close — top right */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/70 backdrop-blur-sm transition-colors hover:bg-white/15 hover:text-white"
        >
          <X size={16} />
        </button>

        {url ? (
          <motion.img
            key={url}
            src={url}
            alt={card.name}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.18 }}
            className="max-h-full max-w-full object-contain"
            style={{
              filter:
                "drop-shadow(0 24px 60px rgba(0,0,0,0.6)) drop-shadow(0 0 40px rgba(201,168,76,0.15))",
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <p className="text-sm text-white/60">No image available.</p>
        )}

        {backUrl && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowBack((v) => !v);
            }}
            className="absolute bottom-6 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-[12px] text-white/70 backdrop-blur-sm transition-colors hover:bg-white/15 hover:text-white"
          >
            <RefreshCw size={11} />
            {showBack ? "Show front" : "Show back"}
          </button>
        )}

        {/* Hint */}
        <p className="pointer-events-none absolute bottom-2 right-4 text-[9px] text-white/30">
          esc · click outside to close
        </p>
      </motion.div>
    </AnimatePresence>
  );
}
